import { useEffect, useState } from "react";
import { Timer, Wheat, LandPlot, Beef, Sun, Handshake, LineChart, Store, Mail, MessageCircle } from "lucide-react";
import { api, fileUrl, waLink } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHero, SectionTitle } from "@/components/site/PageHero";

const BENEFITS = [
  { icon: Timer, title: "Terminación más temprana", text: "Menos tiempo para alcanzar las condiciones comerciales de faena." },
  { icon: Wheat, title: "Mayor eficiencia alimenticia", text: "Mejor conversión del alimento en kilogramos de carne." },
  { icon: LandPlot, title: "Mayor carga animal potencial", text: "Mejor aprovechamiento del forraje con animales de requerimientos de mantenimiento moderados." },
  { icon: Beef, title: "Mejor rendimiento de canal", text: "Excelente musculatura, conformación y calidad carnicera." },
  { icon: Sun, title: "Adaptación tropical", text: "Rusticidad, fertilidad y funcionalidad bajo calor y en sistemas extensivos." },
];

const MODELS = [
  { icon: Handshake, title: "Convenios genéticos", text: "Aportamos embriones, semen y acompañamiento técnico, con participación conjunta en las crías." },
  { icon: LineChart, title: "Evaluación productiva", text: "Seguimiento de crecimiento, fertilidad, adaptación tropical, eficiencia y calidad de carcasa para identificar y multiplicar animales superiores." },
  { icon: Store, title: "Desarrollo comercial", text: "Integración de productores para lograr volúmenes de ganado homogéneo, llegar a compradores especializados y abrir mercados nacionales e internacionales." },
];

const Para = ({ children }) => <p className="text-[#5C635E] leading-relaxed">{children}</p>;

const IconCard = ({ icon: Icon, title, text, testid }) => (
  <div className="card-fv p-7" data-testid={testid}>
    <Icon className="text-[#8B2B22]" size={26} />
    <h3 className="f-display mt-4 text-xl">{title}</h3>
    <p className="mt-2 text-sm text-[#5C635E] leading-relaxed">{text}</p>
  </div>
);

function Intro({ photo }) {
  return (
    <section className="container-fv py-20 sm:py-28 grid gap-14 lg:grid-cols-12 items-center" data-testid="brangus-intro">
      <div className="lg:col-span-7 space-y-5">
        <SectionTitle eyebrow="Nuestro enfoque" title="El mejoramiento genético como motor de la ganadería tropical" />
        <div className="pt-4 space-y-5">
          <Para>En Hacienda Fernández &amp; Vargas apostamos por el mejoramiento genético para transformar la productividad de la ganadería tropical.</Para>
          <Para>Desarrollamos animales de <strong className="text-[#1D3324]">tamaño moderado, excelente conformación carnicera, mayor precocidad y mejor conversión alimenticia</strong>, capaces de llegar a su terminación comercial en menos tiempo y de aumentar la productividad por hectárea.</Para>
        </div>
      </div>
      <div className="lg:col-span-5">
        {photo && <img src={fileUrl(photo.url)} alt={photo.title} className="aspect-square w-full object-cover" data-testid="brangus-intro-photo" />}
      </div>
    </section>
  );
}

function Origin() {
  return (
    <section className="bg-[#F3EAD7]/60 py-20 sm:py-28" data-testid="brangus-origin">
      <div className="container-fv grid gap-12 lg:grid-cols-2">
        <div className="space-y-5">
          <SectionTitle eyebrow="La raza" title="Lo mejor del Angus y del Brahman" />
          <div className="pt-4"><Para>El Brangus combina las cualidades carniceras del Angus con la rusticidad y la adaptación tropical del Brahman. Integra fertilidad, precocidad, calidad de carne y tolerancia a ambientes exigentes.</Para></div>
        </div>
        <div className="space-y-5">
          <SectionTitle eyebrow="El liderazgo argentino" title="Genética argentina de alto valor" />
          <div className="pt-4 space-y-5">
            <Para>Argentina es un referente mundial en genética Brangus, con décadas de selección orientada a la conformación muscular, la eficiencia reproductiva, la calidad de canal y la funcionalidad.</Para>
            <Para>Incorporamos líneas argentinas seleccionadas por su mayor adaptación al trópico y sus características cebuinas funcionales, sin sacrificar calidad de carne ni precocidad.</Para>
          </div>
        </div>
      </div>
    </section>
  );
}

function Benefits() {
  return (
    <section className="container-fv py-20 sm:py-28" data-testid="brangus-benefits">
      <SectionTitle eyebrow="Más productividad por hectárea" title="No buscamos animales más grandes, sino más eficientes" className="max-w-3xl" />
      <p className="mt-6 max-w-3xl text-[#5C635E] leading-relaxed">Seleccionamos animales equilibrados, profundos, musculosos y de estructura moderada, que permiten:</p>
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {BENEFITS.map((b, i) => <IconCard key={b.title} {...b} testid={`brangus-benefit-${i}`} />)}
      </div>
      <blockquote className="mt-14 border-l-4 border-[#C5A059] pl-6 f-display text-2xl sm:text-3xl text-[#1D3324] max-w-4xl" data-testid="brangus-vision">
        Nuestra visión: producir más carne de calidad por hectárea, en menos tiempo y con mayor rentabilidad, para los mercados nacionales e internacionales.
      </blockquote>
    </section>
  );
}

function Crossbreeding() {
  return (
    <section className="bg-[#1D3324] py-20 sm:py-24 text-stone-100 relative grain" data-testid="brangus-crossbreeding">
      <div className="container-fv relative grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="eyebrow !text-[#C5A059]">Mejoramiento genético y cruzamientos</p>
          <h2 className="f-display mt-3 text-3xl sm:text-4xl">Brangus sobre la base cebuina</h2>
        </div>
        <div className="lg:col-span-7 space-y-5 text-stone-300 leading-relaxed">
          <p>Promovemos la incorporación de genética Brangus en hatos cebuinos, especialmente Brahman, Nelore y sus cruzamientos, mediante inseminación artificial, transferencia de embriones y programas de selección.</p>
          <p>El objetivo es mejorar la precocidad, la fertilidad, la eficiencia alimenticia, la conformación carnicera y la calidad de carne, conservando la adaptación que exige el trópico.</p>
        </div>
      </div>
    </section>
  );
}

function Alliances() {
  return (
    <section className="container-fv py-20 sm:py-28" data-testid="brangus-alliances">
      <SectionTitle eyebrow="Costa Rica, Centroamérica y México" title="Convenios genéticos: compartimos genética, inversión y resultados" className="max-w-3xl" />
      <div className="mt-8 grid gap-5 lg:grid-cols-2 max-w-5xl">
        <Para>Buscamos alianzas con ganaderos, fincas y organizaciones interesadas en establecer núcleos de mejoramiento y multiplicación genética Brangus, mediante transferencia de embriones, inseminación artificial y programas de selección.</Para>
        <Para>Nosotros aportamos embriones de genética Brangus argentina seleccionada; nuestros socios aportan las hembras receptoras, la infraestructura y el manejo productivo.</Para>
      </div>
      <h3 className="mt-14 eyebrow">Modelos de colaboración</h3>
      <div className="mt-6 grid gap-6 md:grid-cols-3">
        {MODELS.map((m, i) => <IconCard key={m.title} {...m} testid={`brangus-model-${i}`} />)}
      </div>
      <p className="mt-12 f-display text-xl sm:text-2xl text-[#8B2B22] max-w-3xl">Que la genética se traduzca en mayor rentabilidad para el productor y carne de mejor calidad para el consumidor.</p>
    </section>
  );
}

function CallToAction({ settings }) {
  const subject = "Propuesta de alianza genética Brangus";
  return (
    <section className="bg-[#8B2B22] text-white py-20 sm:py-24" data-testid="brangus-cta">
      <div className="container-fv max-w-4xl">
        <h2 className="f-display text-3xl sm:text-4xl">¿Tiene un proyecto de cría y mejoramiento genético con Brangus?</h2>
        <p className="mt-5 text-white/85 leading-relaxed">Si desea explorar una alianza genética o comercial, compártanos su propuesta. Evaluaremos oportunidades de colaboración y participación conjunta, incluido el aporte de nuestra genética, para desarrollar proyectos innovadores y rentables.</p>
        <div className="mt-10 flex flex-wrap gap-4">
          <a href={waLink(settings.whatsapp, "Hola, tengo un proyecto de cría y mejoramiento genético con Brangus y quisiera explorar una alianza genética Brangus.")} target="_blank" rel="noreferrer" className="btn-fv btn-wa" data-testid="brangus-cta-whatsapp">
            <MessageCircle size={18} /> Escribir por WhatsApp
          </a>
          <a href={`mailto:${settings.email}?subject=${encodeURIComponent(subject)}`} className="btn-fv bg-white text-[#8B2B22] hover:bg-[#F3EAD7]" data-testid="brangus-cta-email">
            <Mail size={18} /> Enviar propuesta por email
          </a>
        </div>
      </div>
    </section>
  );
}

export default function Brangus() {
  const { settings } = useApp();
  const [imgs, setImgs] = useState([]);
  useEffect(() => { api.get("/media").then((r) => setImgs(r.data.filter((m) => m.type === "image"))); }, []);
  const agucho = imgs.find((m) => /Agucho/.test(m.title));
  const luquense = imgs.find((m) => /Luquense/.test(m.title));

  return (
    <div data-testid="brangus-page">
      <PageHero testid="brangus-hero" eyebrow="Brangus" title="Genética superior para una ganadería tropical más eficiente y rentable" image={fileUrl(luquense?.url || agucho?.url)} />
      <Intro photo={agucho} />
      <Origin />
      <Benefits />
      <Crossbreeding />
      <Alliances />
      <CallToAction settings={settings} />
    </div>
  );
}
